import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import axios from '../utils/axiosInstance';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { localeLabel } from '../utils/localeFlag';

const GLOSSARY_CATEGORIES = ['brand', 'model', 'collection', 'material', 'technical'];

export default function Settings() {
  const [prompts, setPrompts] = useState([]);
  const [shopLocales, setShopLocales] = useState([]);
  const [selectedLocale, setSelectedLocale] = useState('');
  const [editingPrompt, setEditingPrompt] = useState('');
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const [glossaryTerms, setGlossaryTerms] = useState([]);
  const [newTerm, setNewTerm] = useState('');
  const [newCategory, setNewCategory] = useState('brand');
  const [glossaryError, setGlossaryError] = useState('');
  const [addingTerm, setAddingTerm] = useState(false);

  const [fieldDefaults, setFieldDefaults] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get('api/transly/prompts/'),
      axios.get('api/transly/locales/'),
      axios.get('api/transly/glossary/'),
      axios.get('api/transly/field-defaults/'),
    ]).then(([promptsRes, localesRes, glossaryRes, fieldDefaultsRes]) => {
      setPrompts(promptsRes.data);
      setShopLocales(localesRes.data);
      setGlossaryTerms(glossaryRes.data);
      setFieldDefaults(fieldDefaultsRes.data?.defaults || []);
      const nonPrimary = localesRes.data.filter((l) => !l.primary);
      if (nonPrimary.length > 0) setSelectedLocale(nonPrimary[0].locale);
    }).catch((err) => console.error('Failed to load settings:', err));
  }, []);

  const handleFieldDefaultChange = async (resourceType, fieldKey, rawPolicy) => {
    const policy = rawPolicy === '' ? null : rawPolicy;
    const prev = fieldDefaults;
    setFieldDefaults((cur) => cur.map((d) =>
      d.resource_type === resourceType && d.field_key === fieldKey
        ? { ...d, translation_policy: policy }
        : d,
    ));
    try {
      await axios.patch(
        `api/transly/field-defaults/${encodeURIComponent(resourceType)}/${encodeURIComponent(fieldKey)}/`,
        { translation_policy: policy },
      );
    } catch (err) {
      setFieldDefaults(prev);
      alert('Failed to update field default: ' + (err.response?.data?.error || err.message));
    }
  };

  useEffect(() => {
    const prompt = prompts.find((p) => p.locale === selectedLocale && p.prompt_type === 'translator');
    setEditingPrompt(prompt?.system_prompt || '');
  }, [selectedLocale, prompts]);

  const handleSavePrompt = async () => {
    setSaving(true);
    try {
      await axios.post('api/transly/prompts/', { locale: selectedLocale, promptType: 'translator', systemPrompt: editingPrompt });
      const res = await axios.get('api/transly/prompts/');
      setPrompts(res.data);
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSeedPrompts = async () => {
    setSeeding(true);
    try {
      await axios.post('api/transly/prompts/seed/');
      const res = await axios.get('api/transly/prompts/');
      setPrompts(res.data);
    } catch (err) {
      alert('Failed to seed: ' + err.message);
    } finally {
      setSeeding(false);
    }
  };

  const handleAddGlossary = async () => {
    if (!newTerm.trim()) return;
    setAddingTerm(true);
    setGlossaryError('');
    try {
      const res = await axios.post('api/transly/glossary/', { term: newTerm.trim(), category: newCategory });
      setGlossaryTerms((prev) => [...prev, res.data]);
      setNewTerm('');
    } catch (err) {
      setGlossaryError(err.response?.data?.error || err.message);
    } finally {
      setAddingTerm(false);
    }
  };

  const handleDeleteGlossary = async (id) => {
    try {
      await axios.delete('api/transly/glossary/', { data: { id } });
      setGlossaryTerms((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const groupedTerms = GLOSSARY_CATEGORIES.reduce((acc, cat) => {
    acc[cat] = glossaryTerms.filter((t) => t.category === cat);
    return acc;
  }, {});

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-foreground mb-6">Settings</h1>

      {/* Locales info */}
      <Card className="mb-6">
        <CardHeader><CardTitle>Shopify Locales</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {shopLocales.map((l) => (
              <span
                key={l.locale}
                className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${
                  l.primary ? 'bg-primary/10 text-primary' :
                  l.published ? 'bg-emerald-500/10 text-emerald-600' :
                  'bg-muted text-muted-foreground'
                }`}
              >
                {localeLabel(l.locale)} - {l.name}
                {l.primary && ' (primary)'}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Prompt editor */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Translation Prompts</CardTitle>
            {prompts.length === 0 && (
              <Button variant="secondary" size="sm" onClick={handleSeedPrompts} disabled={seeding}>
                {seeding ? 'Seeding...' : 'Seed Default Prompts'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            {shopLocales.filter((l) => !l.primary).map((l) => (
              <Button key={l.locale} variant={selectedLocale === l.locale ? 'default' : 'outline'} size="sm" onClick={() => setSelectedLocale(l.locale)}>
                {localeLabel(l.locale)}
              </Button>
            ))}
          </div>

          <textarea
            value={editingPrompt}
            onChange={(e) => setEditingPrompt(e.target.value)}
            rows={16}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring font-mono"
            placeholder="Enter the system prompt for this locale..."
          />

          <Button className="mt-3" onClick={handleSavePrompt} disabled={saving}>
            {saving ? 'Saving...' : 'Save Prompt'}
          </Button>
        </CardContent>
      </Card>

      {/* Glossary */}
      <Card>
        <CardHeader><CardTitle>Glossary (Do Not Translate)</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <Input
              value={newTerm}
              onChange={(e) => { setNewTerm(e.target.value); setGlossaryError(''); }}
              placeholder="New term..."
              className="flex-1"
            />
            <select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              {GLOSSARY_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <Button onClick={handleAddGlossary} disabled={addingTerm || !newTerm.trim()}>
              {addingTerm ? 'Adding...' : 'Add'}
            </Button>
          </div>
          {glossaryError && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive mb-4">{glossaryError}</div>
          )}

          {GLOSSARY_CATEGORIES.map((cat) => {
            const terms = groupedTerms[cat];
            if (!terms || terms.length === 0) return null;
            return (
              <div key={cat} className="mb-4">
                <Label className="text-xs uppercase text-muted-foreground mb-2 block">{cat}</Label>
                <div className="flex flex-wrap gap-2">
                  {terms.map((t) => (
                    <span key={t.id} className="inline-flex items-center gap-1 bg-muted rounded-md px-2.5 py-1 text-sm text-foreground">
                      {t.term}
                      <button onClick={() => handleDeleteGlossary(t.id)} className="text-muted-foreground hover:text-destructive ml-1 transition-colors">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Field defaults — per (resource_type, field_key) translation policy.
          Applies as a fallback when a per-row policy isn't set, and is honored
          by the translation pipeline so e.g. setting PRODUCT.handle to skip
          stops handle from being translated for every product. */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Field Defaults</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Default translation policy per resource type and field. Applies to every resource of that type. Per-row overrides (where supported) take precedence.
          </p>
        </CardHeader>
        <CardContent>
          {Object.entries(
            fieldDefaults.reduce((acc, d) => {
              (acc[d.resource_type] = acc[d.resource_type] || []).push(d);
              return acc;
            }, {}),
          ).map(([resourceType, rows]) => (
            <div key={resourceType} className="mb-4 last:mb-0">
              <Label className="text-xs uppercase text-muted-foreground mb-2 block">{resourceType}</Label>
              <div className="space-y-1">
                {rows.map((row) => (
                  <div
                    key={`${row.resource_type}:${row.field_key}`}
                    className="flex items-center justify-between gap-3 py-1.5 px-2 rounded hover:bg-muted/40"
                  >
                    <span className="text-sm font-mono">{row.field_key}</span>
                    <select
                      value={row.translation_policy ?? ''}
                      onChange={(e) => handleFieldDefaultChange(row.resource_type, row.field_key, e.target.value)}
                      className="border border-input bg-background h-8 rounded-md px-2 text-sm"
                    >
                      <option value="">Unset (translate)</option>
                      <option value="translate">Translate</option>
                      <option value="skip">Skip</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
